import { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { documentsAPI, type Document, type CategoryWithDocuments } from '../../lib/api';
import { toast } from 'sonner';

/** Where a notification link asked the detail pop-up to open. */
export interface DocumentDeepLink {
  /** Tab to open on, e.g. 'communications' (CR-8197). Undefined = Details. */
  tab?: string;
  /** Conversation to open on the Communications tab (owner side only). */
  conversation: number | null;
}

interface UseDocumentDeepLinkArgs {
  documents: Document[] | undefined;
  categoriesWithDocs: CategoryWithDocuments[] | undefined;
  loading: boolean;
  onOpen: (doc: Document) => void;
}

const NO_DEEP_LINK: DocumentDeepLink = { conversation: null };

/**
 * Open a document's detail pop-up from a notification link.
 *
 * CR-5462: links carry `?doc=<id>`; CR-8197 adds `&tab=communications` and
 * `&conversation=<company id>`. The document is looked up in whatever the page
 * has loaded (folder-grouped or flat) and, failing that — another folder, a
 * later page, an active filter — fetched by id, so a link always lands. The
 * params are stripped once handled so back/forward or a refresh doesn't
 * re-open; clearing the guard when they are gone lets a second click on a
 * link to the same document open it again.
 *
 * @example
 * const { deepLink, clearDeepLink } = useDocumentDeepLink({ documents, categoriesWithDocs, loading, onOpen: handleOpenDetail });
 *
 * @param args - The page's loaded documents, its loading flag and the opener
 * @returns The tab/conversation the link asked for, and a reset for when the pop-up closes
 */
export function useDocumentDeepLink({
  documents, categoriesWithDocs, loading, onOpen,
}: UseDocumentDeepLinkArgs) {
  const [searchParams, setSearchParams] = useSearchParams();
  const handledDocRef = useRef<string | null>(null);
  const [deepLink, setDeepLink] = useState<DocumentDeepLink>(NO_DEEP_LINK);

  useEffect(() => {
    const docParam = searchParams.get('doc');
    if (!docParam) {
      handledDocRef.current = null;
      return;
    }
    if (loading || handledDocRef.current === docParam) return;
    handledDocRef.current = docParam;

    const link: DocumentDeepLink = {
      tab: searchParams.get('tab') ?? undefined,
      conversation: Number(searchParams.get('conversation')) || null,
    };
    const strip = () => {
      const next = new URLSearchParams(searchParams);
      ['doc', 'tab', 'conversation'].forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    };
    const open = (doc: Document) => {
      setDeepLink(link);
      onOpen(doc);
    };

    let target = documents?.find((d) => String(d.id) === docParam);
    if (!target && categoriesWithDocs?.length) {
      for (const cat of categoriesWithDocs) {
        const found = cat.documents?.find((d) => String(d.id) === docParam);
        if (found) { target = found; break; }
      }
    }
    if (target) {
      open(target);
      strip();
      return;
    }
    documentsAPI.getDocument(Number(docParam))
      .then((res) => open(res.data))
      .catch((err: unknown) => {
        console.error('Failed to open linked document', err);
        toast.error('That document is not available to you.');
      })
      .finally(strip);
  }, [searchParams, documents, categoriesWithDocs, loading, onOpen, setSearchParams]);

  const clearDeepLink = useCallback(() => setDeepLink(NO_DEEP_LINK), []);

  return { deepLink, clearDeepLink };
}
